import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, MailCheck, ArrowLeft, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG =
  "https://images.unsplash.com/photo-1688891648910-1e056d2377c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxub3JkaWMlMjBhYnN0cmFjdCUyMG5hdHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MTY1OTg3fDA&ixlib=rb-4.1.0&q=85";

export default function GlemtPassord() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {
      // Vi viser alltid samme melding for å ikke avsløre om e-posten finnes
    } finally {
      setSent(true);
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen bg-background">
      <div className="absolute right-5 top-5 z-20">
        <ThemeToggle />
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[46%] lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto w-full max-w-sm"
        >
          <Logo subtitle="Styrerom & Aksjeeierbok" />

          {sent ? (
            <div className="mt-12" data-testid="forgot-sent">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
                <MailCheck className="h-6 w-6" />
              </div>
              <h1 className="mt-6 font-headings text-3xl font-extrabold tracking-tight text-foreground">Sjekk e-posten din</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Hvis <span className="font-medium text-foreground">{email}</span> finnes hos oss, har vi sendt en lenke
                for å velge nytt passord. Lenken er gyldig i én time.
              </p>
              <Link to="/login" data-testid="forgot-back-login-link" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                <ArrowLeft className="h-4 w-4" /> Tilbake til innlogging
              </Link>
            </div>
          ) : (
            <>
              <div className="mt-12">
                <h1 className="font-headings text-3xl font-extrabold tracking-tight text-foreground">Glemt passord?</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Skriv inn e-postadressen din, så sender vi deg en lenke for å velge et nytt passord.
                </p>
              </div>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs font-medium">E-post</Label>
                  <Input
                    id="email" data-testid="forgot-email-input" type="email" autoComplete="email"
                    placeholder="din@epost.no" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 h-11"
                  />
                </div>

                <Button type="submit" data-testid="forgot-submit-button" disabled={loading} className="h-11 w-full gap-2 text-sm font-semibold active:scale-[0.98]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {loading ? "Sender …" : "Send tilbakestillingslenke"}
                </Button>
              </form>

              <p className="mt-6 text-sm text-muted-foreground">
                Kom du på passordet?{" "}
                <Link to="/login" data-testid="forgot-login-link" className="font-semibold text-primary hover:underline">Logg inn</Link>
              </p>
            </>
          )}

          <p className="mt-10 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> Kryptert · ISO 27001 · Data lagret i Norge
          </p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block lg:w-[54%]">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/70 via-primary/30 to-transparent" />
      </div>
    </div>
  );
}
